import type {
  TChatAskPayload,
  TErrorPayload,
} from '@cascade/api-client/events';
import { useCallback, useEffect, useReducer, useRef } from 'react';
import type {
  TDispatchUpdate,
  TSendRequest,
  TSendRequestOptions,
} from './useConversationState.types.ts';
import * as helpers from './useConversationState.helpers.ts';
import {
  createInitialState,
  reduceConversation,
} from '#providers/conversations/Conversations.reducer.ts';

export const useConversationState = (conversationId?: string) => {
  const [state, dispatch] = useReducer(
    reduceConversation,
    conversationId,
    createInitialState,
  );

  const dispatchUpdate: TDispatchUpdate = dispatch;
  const isAskingRef = useRef(false);
  const hasPendingAnswer = helpers.hasPendingAnswer(state.messages);

  const failAnswer = useCallback((requestId: string, error: TErrorPayload) => {
    dispatch({
      type: 'chat.answer.error',
      envelope: {
        type: 'chat.answer.error',
        request_id: requestId,
        payload: error,
      },
    });
  }, []);

  const sendRequest = useCallback(
    async (
      payload: TChatAskPayload,
      send: TSendRequest,
      { files }: TSendRequestOptions = {},
    ) => {
      if (isAskingRef.current) {
        throw new Error('An answer is still in progress');
      }

      const requestId = helpers.createRequestId();

      isAskingRef.current = true;
      dispatch({
        type: 'chat.ask',
        envelope: { type: 'chat.ask', request_id: requestId, payload },
        files,
      });

      try {
        await send(requestId);
      } catch (error) {
        isAskingRef.current = false;

        const errorPayload = helpers.toErrorPayload(error);

        failAnswer(requestId, errorPayload);

        throw error;
      }
    },
    [failAnswer],
  );

  const removeRequest = useCallback((requestId: string) => {
    dispatch({ type: 'chat.ask.removed', requestId });
  }, []);

  useEffect(() => {
    isAskingRef.current = hasPendingAnswer;
  });

  return {
    state,
    hasPendingAnswer,
    dispatchUpdate,
    sendRequest,
    removeRequest,
    failAnswer,
  };
};
